import React, { useState, useEffect } from "react";
import { getAccountNootSettings, saveAccountNootSettings, getAccountNootSyncLogs } from "services/company";


const AccountingIntegrationSection = () => {
    const [settings, setSettings] = useState({ apiKey: "", apiSecret: "", tenantId: "", baseUrl: "https://accountnoot.com/api/v1", isEnabled: false });
    const [hasApiKey, setHasApiKey] = useState(false);
    const [hasApiSecret, setHasApiSecret] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [showSecret, setShowSecret] = useState(false);
    const [syncLogs, setSyncLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    useEffect(() => {
        getAccountNootSettings().then(data => {
            if (data) {
                setHasApiKey(data.hasApiKey || false);
                setHasApiSecret(data.hasApiSecret || false);
                setSettings({
                    apiKey: "",
                    apiSecret: "",
                    tenantId: data.tenantId || "",
                    baseUrl: data.baseUrl || "https://accountnoot.com/api/v1",
                    isEnabled: data.isEnabled || false,
                });
            }
            setLoading(false);
        });
        setLogsLoading(true);
        getAccountNootSyncLogs(10).then(data => {
            setSyncLogs(data || []);
            setLogsLoading(false);
        });
    }, []);

    const handleSave = async () => {
        setSaving(true);
        setSaveMsg(null);
        try {
            await saveAccountNootSettings({
                apiKey: settings.apiKey || undefined,
                apiSecret: settings.apiSecret || undefined,
                tenantId: settings.tenantId,
                baseUrl: settings.baseUrl,
                isEnabled: settings.isEnabled,
            });
            setSaveMsg({ type: "success", text: "Settings saved successfully." });
            const refreshed = await getAccountNootSettings();
            if (refreshed) {
                setHasApiKey(refreshed.hasApiKey || false);
                setHasApiSecret(refreshed.hasApiSecret || false);
                setSettings(s => ({
                    ...s,
                    apiKey: "",
                    apiSecret: "",
                    isEnabled: refreshed.isEnabled ?? s.isEnabled,
                    tenantId: refreshed.tenantId || s.tenantId,
                    baseUrl: refreshed.baseUrl || s.baseUrl,
                }));
            }
        } catch (e) {
            setSaveMsg({ type: "error", text: "Failed to save settings. Please try again." });
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = {
        width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
        border: "1px solid rgba(255,255,255,0.12)", backgroundColor: "rgba(255,255,255,0.06)",
        color: "inherit", outline: "none",
    };

    const labelStyle = { fontSize: 12, fontWeight: 600, marginBottom: 4, display: "block", opacity: 0.75 };

    if (loading) return <p className="title">Loading integration settings...</p>;

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="sub-title">Accounting Integrations</h2>
            </div>

            {/* AccountNoot Integration */}
            <div className="p-4 rounded-3 mb-4" style={{ border: "1px solid rgba(99,179,237,0.3)", backgroundColor: "rgba(99,179,237,0.05)" }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div style={{
                        width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                        backgroundColor: "rgba(99,179,237,0.15)", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        color: "#63b3ed", fontWeight: 800, fontSize: 14,
                        border: "1px solid rgba(99,179,237,0.3)",
                    }}>AN</div>
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <p className="m-0 sub-titles" style={{ fontWeight: 700 }}>AccountNoot</p>
                            <span style={{
                                fontSize: 10, padding: "2px 8px", borderRadius: 8, fontWeight: 700,
                                backgroundColor: settings.isEnabled ? "rgba(72,187,120,0.15)" : "rgba(255,255,255,0.07)",
                                color: settings.isEnabled ? "#48bb78" : "#a0aec0",
                                border: settings.isEnabled ? "1px solid rgba(72,187,120,0.3)" : "1px solid rgba(255,255,255,0.1)",
                            }}>
                                {settings.isEnabled ? "Active" : "Inactive"}
                            </span>
                        </div>
                        <p className="m-0 title mt-1" style={{ fontSize: 12 }}>
                            Automatically sync payroll data to AccountNoot after every payroll approval — journal entries, paychecks, tax liabilities, and more.
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2" style={{ cursor: "pointer", flexShrink: 0 }}
                        onClick={() => setSettings(s => ({ ...s, isEnabled: !s.isEnabled }))}
                    >
                        <span className="title" style={{ fontSize: 12 }}>{settings.isEnabled ? "Enabled" : "Disabled"}</span>
                        <div
                            style={{
                                width: 40, height: 22, borderRadius: 11, cursor: "pointer", transition: "background 0.2s",
                                backgroundColor: settings.isEnabled ? "#48bb78" : "#a0aec0",
                                position: "relative", flexShrink: 0,
                            }}
                        >
                            <div style={{
                                position: "absolute", top: 3, left: settings.isEnabled ? 20 : 3,
                                width: 16, height: 16, borderRadius: "50%", backgroundColor: "#fff", transition: "left 0.2s",
                            }} />
                        </div>
                    </div>
                </div>

                <div className="row g-3">
                    <div className="col-md-6">
                        <label style={labelStyle}>
                            API Key {hasApiKey && <span style={{ color: "#48bb78", fontWeight: 400 }}>(saved)</span>}
                        </label>
                        <input
                            type="text"
                            style={inputStyle}
                            placeholder={hasApiKey ? "Enter new key to replace..." : "Enter API Key"}
                            value={settings.apiKey}
                            onChange={e => setSettings(s => ({ ...s, apiKey: e.target.value }))}
                            autoComplete="off"
                        />
                    </div>
                    <div className="col-md-6">
                        <label style={labelStyle}>
                            API Secret {hasApiSecret && <span style={{ color: "#48bb78", fontWeight: 400 }}>(saved)</span>}
                        </label>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showSecret ? "text" : "password"}
                                style={{ ...inputStyle, paddingRight: 40 }}
                                placeholder={hasApiSecret ? "Enter new secret to replace..." : "Enter API Secret"}
                                value={settings.apiSecret}
                                onChange={e => setSettings(s => ({ ...s, apiSecret: e.target.value }))}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowSecret(v => !v)}
                                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#a0aec0", fontSize: 12 }}
                            >
                                {showSecret ? "Hide" : "Show"}
                            </button>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label style={labelStyle}>Tenant ID</label>
                        <input
                            type="text"
                            style={inputStyle}
                            placeholder="AccountNoot Tenant ID"
                            value={settings.tenantId}
                            onChange={e => setSettings(s => ({ ...s, tenantId: e.target.value }))}
                        />
                    </div>
                    <div className="col-md-6">
                        <label style={labelStyle}>Base URL</label>
                        <input
                            type="text"
                            style={inputStyle}
                            value={settings.baseUrl}
                            onChange={e => setSettings(s => ({ ...s, baseUrl: e.target.value }))}
                        />
                    </div>
                </div>

                {saveMsg && (
                    <div className="mt-3" style={{
                        padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500,
                        backgroundColor: saveMsg.type === "success" ? "rgba(72,187,120,0.12)" : "rgba(245,101,101,0.12)",
                        color: saveMsg.type === "success" ? "#48bb78" : "#fc8181",
                        border: `1px solid ${saveMsg.type === "success" ? "rgba(72,187,120,0.3)" : "rgba(245,101,101,0.3)"}`,
                    }}>
                        {saveMsg.text}
                    </div>
                )}

                <div className="mt-3 d-flex align-items-center gap-3">
                    <button
                        className="button-orange py-2 px-4 rounded-pill"
                        style={{ fontSize: 13, fontWeight: 600 }}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Settings"}
                    </button>
                    <p className="m-0 title" style={{ fontSize: 12 }}>
                        Payroll data is sent automatically after each payroll approval. You can also manually sync from any approved payroll run.
                    </p>
                </div>

                <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <p className="m-0 title" style={{ fontSize: 12, opacity: 0.7 }}>
                        <strong>What gets synced:</strong> Company info, employees, payroll run summary, paycheck details, tax liabilities, and a balanced journal batch (sent as review_only — AccountNoot staff reviews before posting).
                    </p>
                </div>
            </div>

            {/* Recent Syncs */}
            <div className="mt-4">
                <h2 className="sub-title mb-3">Recent Syncs</h2>
                {logsLoading ? (
                    <p className="title" style={{ fontSize: 13 }}>Loading sync history...</p>
                ) : syncLogs.length === 0 ? (
                    <div className="p-4 rounded-3 text-center" style={{ border: "1px solid rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                        <p className="title m-0" style={{ fontSize: 13 }}>No syncs yet. Approve a payroll to trigger the first sync.</p>
                    </div>
                ) : (
                    <div className="rounded-3" style={{ border: "1px solid rgba(255,255,255,0.08)", overflow: "hidden" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                            <thead>
                                <tr style={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                                    {["Payroll ID", "Status", "Employees", "Gross Pay", "Net Pay", "Taxes", "Deductions", "Synced At"].map(h => (
                                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, opacity: 0.6, whiteSpace: "nowrap", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {syncLogs.map((log, i) => {
                                    const isSuccess = log.status === "success";
                                    const isFailed = log.status === "failed";
                                    return (
                                        <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                                            <td style={{ padding: "10px 14px", fontFamily: "monospace", opacity: 0.85 }}>{log.payrollId}</td>
                                            <td style={{ padding: "10px 14px" }}>
                                                <span style={{
                                                    fontSize: 11, padding: "2px 8px", borderRadius: 6, fontWeight: 700,
                                                    backgroundColor: isSuccess ? "rgba(72,187,120,0.15)" : isFailed ? "rgba(245,101,101,0.15)" : "rgba(255,255,255,0.07)",
                                                    color: isSuccess ? "#48bb78" : isFailed ? "#fc8181" : "#a0aec0",
                                                    border: `1px solid ${isSuccess ? "rgba(72,187,120,0.3)" : isFailed ? "rgba(245,101,101,0.3)" : "rgba(255,255,255,0.1)"}`,
                                                }}>
                                                    {log.status}
                                                </span>
                                                {log.errorMessage && (
                                                    <span style={{ marginLeft: 6, color: "#fc8181", fontSize: 11 }} title={log.errorMessage}>⚠</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "10px 14px" }}>{log.employeeCount || "—"}</td>
                                            <td style={{ padding: "10px 14px" }}>{log.grossPay ? `$${Number(log.grossPay).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}</td>
                                            <td style={{ padding: "10px 14px" }}>{log.netPay ? `$${Number(log.netPay).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}</td>
                                            <td style={{ padding: "10px 14px" }}>{log.totalTaxes ? `$${Number(log.totalTaxes).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}</td>
                                            <td style={{ padding: "10px 14px" }}>{log.totalDeductions ? `$${Number(log.totalDeductions).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}</td>
                                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap", opacity: 0.7 }}>
                                                {log.syncedAt ? new Date(log.syncedAt).toLocaleString() : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

        </div>
    );
};

export default AccountingIntegrationSection;
